// Patients API service
import { BaseAPI } from './base';
import type {
  Patient,
  CreatePatientRequest,
  UpdatePatientRequest,
  PatientSearchParams,
  PatientSearchResult,
  PatientDashboard,
} from '../../types/patients';
import { buildPatientStudyFilesUrl } from './patientStudyFilesUrl';

class PatientsAPI extends BaseAPI {
  async searchPatients(params: PatientSearchParams): Promise<PatientSearchResult> {
    const searchParams = new URLSearchParams();
    
    // Map per_page to page_size for backend compatibility
    const backendParams = { ...params };
    if (backendParams.per_page) {
      (backendParams as any).page_size = backendParams.per_page;
      delete backendParams.per_page;
    }
    
    Object.entries(backendParams).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        searchParams.append(key, value.toString());
      }
    });

    const response = await this.request<any>({
      method: 'GET',
      url: `/api/v1/patients?${searchParams.toString()}`,
    });

    // Transform backend response to PatientSearchResult format
    return {
      patients: response.patients || [],
      total: response.total || 0,
      offset: ((response.pagination?.page || 1) - 1) * (response.pagination?.page_size || 20),
      limit: response.pagination?.page_size || 20,
      has_more: response.pagination?.has_next || false
    };
  }

  async getPatient(patientId: string, options?: { includeFiles?: boolean }): Promise<Patient> {
    const includeFiles = options?.includeFiles !== false;
    const qs = includeFiles ? '' : '?include_files=false';
    return this.request<Patient>({
      method: 'GET',
      url: `/api/v1/patients/${patientId}${qs}`,
    });
  }

  async createPatient(data: CreatePatientRequest): Promise<Patient> {
    return this.request<Patient>({
      method: 'POST',
      url: '/api/v1/patients',
      data,
    });
  }

  async updatePatient(patientId: string, data: UpdatePatientRequest): Promise<Patient> {
    return this.request<Patient>({
      method: 'PUT',
      url: `/api/v1/patients/${patientId}`,
      data,
    });
  }

  async deletePatient(patientId: string): Promise<void> {
    return this.request<void>({
      method: 'DELETE',
      url: `/api/v1/patients/${patientId}`,
    });
  }

  async getPatientDashboard(patientId: string): Promise<PatientDashboard> {
    return this.request<PatientDashboard>({
      method: 'GET',
      url: `/api/v1/patients/${patientId}/dashboard`,
    });
  }

  async getPatientDicomFiles(patientId: string): Promise<{ dicom_files?: unknown[]; success?: boolean }> {
    return this.request<{ dicom_files?: unknown[]; success?: boolean }>({
      method: 'GET',
      url: `/api/v1/patients/${patientId}/dicom-files`,
    });
  }

  async getPatientStudies(patientId: string): Promise<{
    success?: boolean;
    studies?: Array<{
      group_key: string;
      date: string;
      study_instance_uid?: string;
      title: string;
      image_count: number;
      thumbnail_s3_key?: string;
      exam_type_id?: string;
      worklist_item_id?: string;
      split_eligible?: boolean;
      accession_number?: string;
    }>;
    cached?: boolean;
  }> {
    return this.request({
      method: 'GET',
      url: `/api/v1/patients/${patientId}/studies`,
    });
  }

  async getPatientStudyFiles(
    patientId: string,
    groupKey: string,
    opts?: { scheduledDate?: string }
  ): Promise<{ files?: unknown[]; total?: number; scoped?: boolean }> {
    return this.request({
      method: 'GET',
      url: buildPatientStudyFilesUrl(patientId, groupKey, opts),
    });
  }

  async getPatientReports(patientId: string, includeDrafts: boolean = true): Promise<unknown[]> {
    return this.request<unknown[]>({
      method: 'GET',
      url: `/api/v1/patients/${patientId}/reports?include_drafts=${includeDrafts}`,
    });
  }

  async getPatientS3Files(patientId: string): Promise<unknown[]> {
    return this.request<unknown[]>({
      method: 'GET',
      url: `/api/v1/patients/${patientId}/s3-files`,
    });
  }

  async getImageManifest(patientId: string, refresh: boolean = false): Promise<any> {
    return this.request<any>({
      method: 'GET',
      url: `/api/v1/patients/${patientId}/image-manifest?refresh=${refresh}`,
    });
  }
}

export const patientsAPI = new PatientsAPI();
